import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useUnifiedAuth } from '../../context/UnifiedAuthContext';
import { getQuizHistory } from '../../utils/studentStatsService';
// Note: Direct Firebase imports removed - using studentStatsService for data access
import ViewLayout2 from '../../components/ui/ViewLayout2';
import { Logger } from '../../utils/logger';

const log = Logger.create('StudentQuizHistory');

/**
 * StudentQuizHistory - Shows all past quizzes for a student
 * Allows filtering by course and starting practice mode
 */
function StudentQuizHistory() {
  const { user, userProfile, isAuthenticated, loading } = useUnifiedAuth();
  const navigate = useNavigate();
  
  const [quizHistory, setQuizHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  
  // Redirect if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/?auth=true');
    }
  }, [loading, isAuthenticated, navigate]);
  
  // Load quiz history
  useEffect(() => {
    const loadHistory = async () => {
      if (!user) return;
      
      try {
        const history = await getQuizHistory(user.uid);
        setQuizHistory(history);
        setFilteredHistory(history);
        
        // Extract unique courses
        const courseMap = new Map();
        history.forEach(h => {
          if (h.courseId && h.courseName) {
            courseMap.set(h.courseId, h.courseName);
          }
        });
        setCourses(Array.from(courseMap.entries()).map(([id, name]) => ({ id, name })));
        
      } catch (err) {
        log.error('Error loading quiz history:', err);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadHistory();
  }, [user]);
  
  // Filter by course
  useEffect(() => {
    if (selectedCourse === 'all') {
      setFilteredHistory(quizHistory);
    } else if (selectedCourse === 'none') {
      setFilteredHistory(quizHistory.filter(h => !h.courseId));
    } else {
      setFilteredHistory(quizHistory.filter(h => h.courseId === selectedCourse));
    }
  }, [selectedCourse, quizHistory]);
  
  // Group quizzes by course
  const groupedByCourse = React.useMemo(() => {
    const groups = new Map();
    
    // Group by courseId
    filteredHistory.forEach(quiz => {
      const key = quiz.courseId || 'none';
      if (!groups.has(key)) {
        groups.set(key, {
          courseId: quiz.courseId,
          courseName: quiz.courseName || 'Ohne Kurs',
          quizzes: []
        });
      }
      groups.get(key).quizzes.push(quiz);
    });
    
    // Calculate stats for each group
    groups.forEach(group => {
      const quizzes = group.quizzes;
      group.count = quizzes.length;
      group.avgScore = quizzes.length > 0
        ? Math.round(quizzes.reduce((sum, q) => sum + (q.score || 0), 0) / quizzes.length)
        : 0;
      group.bestScore = quizzes.length > 0
        ? Math.max(...quizzes.map(q => q.score || 0))
        : 0;
      group.totalQuestions = quizzes.reduce((sum, q) => sum + (q.totalQuestions || 0), 0);
      group.correctAnswers = quizzes.reduce((sum, q) => sum + (q.correctAnswers || 0), 0);
    });
    
    // Sort: courses first (alphabetically), then "Ohne Kurs"
    const sorted = Array.from(groups.values()).sort((a, b) => {
      if (a.courseId && !b.courseId) return -1;
      if (!a.courseId && b.courseId) return 1;
      return (a.courseName || '').localeCompare(b.courseName || '');
    });
    
    return sorted;
  }, [filteredHistory]);
  
  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Start practice mode
  const handlePractice = (sessionId, quizName, teacherUserId) => {
    // Navigate to practice mode with session ID and teacher ID for new format
    if (teacherUserId) {
      navigate(`/student/practice/${sessionId}?teacherId=${teacherUserId}`);
    } else {
      navigate(`/student/practice/${sessionId}`);
    }
  };
  
  // Loading state
  if (loading || isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-500 border-t-transparent mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Lade Quiz-Historie...</p>
        </div>
      </div>
    );
  }
  
  // Calculate stats
  const totalQuizzes = filteredHistory.length;
  const avgScore = totalQuizzes > 0 
    ? Math.round(filteredHistory.reduce((sum, h) => sum + (h.score || 0), 0) / totalQuizzes)
    : 0;
  const bestScore = totalQuizzes > 0
    ? Math.max(...filteredHistory.map(h => h.score || 0))
    : 0;
  
  return (
    <ViewLayout2
      title="Quiz-Historie"
      onBack="/student/dashboard"
      maxWidth="7xl"
    >
      {/* Stats Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 text-center">
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{totalQuizzes}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Quizze</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 text-center">
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">{avgScore}%</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Ø Score</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 text-center">
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{bestScore}%</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Bester</div>
        </div>
      </div>
        {/* Filter */}
        {courses.length > 0 && (
          <div>
            <select
              value={selectedCourse}
              onChange={(e) => setSelectedCourse(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white theme-transition"
            >
              <option value="all">Alle Kurse</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>{course.name}</option>
              ))}
              <option value="none">Ohne Kurs</option>
            </select>
          </div>
        )}
        
        {/* Quiz List - Grouped by Course */}
        {filteredHistory.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
            <div className="text-4xl mb-4">
              <svg className="w-16 h-16 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-2">
              Noch keine Quizze absolviert.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              Nimm an einem Quiz teil, um hier deine Historie zu sehen.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {groupedByCourse.map(group => (
              <div key={group.courseId || 'none'} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                {/* Course Header with Stats */}
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-b border-gray-200 dark:border-gray-700 p-4 rounded-t-xl">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {group.courseName}
                    </h3>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {group.count} {group.count === 1 ? 'Quiz' : 'Quizze'}
                    </span>
                  </div>
                  
                  {/* Course Stats */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center border border-gray-200 dark:border-gray-700">
                      <div className="text-xl font-bold text-green-600 dark:text-green-400">
                        {group.avgScore}%
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Durchschnitt</div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center border border-gray-200 dark:border-gray-700">
                      <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                        {group.bestScore}%
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Bester</div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center border border-gray-200 dark:border-gray-700">
                      <div className="text-xl font-bold text-gray-900 dark:text-white">
                        {group.correctAnswers}/{group.totalQuestions}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Richtige</div>
                    </div>
                  </div>
                </div>
                
                {/* Quizzes in this course */}
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {group.quizzes.map((quiz, index) => (
                    <div key={quiz.sessionId || index} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 theme-transition">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 dark:text-white truncate">
                            {quiz.quizName || 'Quiz'}
                          </h4>
                          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500 dark:text-gray-400">
                            <span>{formatDate(quiz.completedAt)}</span>
                            <span>•</span>
                            <span>{quiz.correctAnswers}/{quiz.totalQuestions} richtig</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 ml-4">
                          <div className="text-right">
                            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                              {quiz.score}%
                            </div>
                            {quiz.points > 0 && (
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {quiz.points} Punkte
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => handlePractice(quiz.sessionId, quiz.quizName, quiz.teacherUserId)}
                            className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg theme-transition-fast"
                          >
                            Üben
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
    </ViewLayout2>
  );
}

export default StudentQuizHistory;
